import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SerializedStyles } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HStack, IconButton, TextField } from '@rtkwlf/fenrir-react';
import { faChevronDoubleLeft } from '@rtkwlf/pro-regular-svg-icons/faChevronDoubleLeft';
import { faChevronDoubleRight } from '@rtkwlf/pro-regular-svg-icons/faChevronDoubleRight';
import { faChevronLeft } from '@rtkwlf/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@rtkwlf/pro-regular-svg-icons/faChevronRight';
import { useTranslation } from 'react-i18next';
import type { UsePaginationInstanceProps } from 'react-table';

import { getFormatter } from '@packages/utils/calculation';
import { EventKey } from 'ae-constants/constants';

export interface PaginationProps<D extends Record<string, unknown>>
  extends UsePaginationInstanceProps<D> {
  /**
   * This is the label for Pagination and available only for screen reader users.
   * It should include the context and must include the word Pagination as per the guidelines
   * https://developer.visa.com/pages/accessibility/navigation/nav-007
   * e.g. Pagination for search results.
   */
  ariaLabel?: string;

  /** Emotion based styles */
  styles?: SerializedStyles;

  /** Total pages available for pagination */
  totalPageCount: number;

  /** Currently focused page. */
  pageIndex: number;

  /** Number of rows or items per page */
  pageSize: number;

  /** Total number of rows or items */
  totalRows: number;
}

const formatter = getFormatter();

export const Pagination: React.FC<PaginationProps<Record<string, unknown>>> = ({
  ariaLabel,
  styles,
  canPreviousPage,
  canNextPage,
  nextPage,
  previousPage,
  totalRows,
  gotoPage,
  pageIndex,
  pageSize,
  totalPageCount,
}) => {
  const { t } = useTranslation();
  const currentPage = useMemo(() => pageIndex + 1, [pageIndex]);
  const [pageInputValue, setPageInputValue] = useState<number>(currentPage);

  const page = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (page.current) {
      page.current.value = `${currentPage}`;
    }
    setPageInputValue(currentPage);
  }, [currentPage, page, setPageInputValue]);

  const onPageChange = useCallback(
    (e: any) => {
      const newPage = parseInt(e.target.value, 10);

      if (
        newPage !== pageInputValue &&
        newPage <= totalPageCount &&
        newPage !== 0 &&
        !Number.isNaN(newPage) &&
        page.current
      ) {
        page.current.value = `${newPage}`;
        setPageInputValue(newPage);
        gotoPage(newPage - 1);
      } else if (page.current) {
        page.current.value = `${pageInputValue}`;
      }
    },
    [totalPageCount, gotoPage, pageInputValue, page],
  );

  const handleReturnKey = useCallback((event: any): void => {
    if (event.key === EventKey.Return) {
      event.target.blur();
    }
  }, []);

  return (
    <HStack
      asChild
      alignItems="center"
      justifyContent="flex-end"
      textColor="body"
      gap="small"
      paddingTop="default"
      fontSize="small"
    >
      <nav css={[styles]} role="navigation" aria-label={ariaLabel}>
        {totalRows === 0
          ? null
          : t('ui:pagination.resultString', {
              end: formatter.format(
                Math.min(totalRows, currentPage * pageSize),
              ),
              start: formatter.format(pageIndex * pageSize + 1),
              total: formatter.format(totalRows),
            })}

        {totalRows > pageSize ? (
          <>
            <IconButton
              aria-label={t('icon:firstPage')}
              data-testid="first-page-button"
              onClick={() => gotoPage(0)}
              isDisabled={!canPreviousPage}
            >
              <FontAwesomeIcon icon={faChevronDoubleLeft} />
            </IconButton>

            <IconButton
              aria-label={t('icon:prevPage')}
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
              marginLeft="zero"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </IconButton>

            <HStack gap="small" whiteSpace="nowrap">
              <TextField
                ref={page}
                defaultValue={pageInputValue}
                onBlur={onPageChange}
                onKeyUp={handleReturnKey}
                textAlign="center"
                maxWidth="2rem"
              />
              {t('ui:pagination.pageDetails', {
                totalPageCount: formatter.format(totalPageCount),
              })}
            </HStack>

            <IconButton
              aria-label={t('icon:nextPage')}
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </IconButton>

            <IconButton
              aria-label={t('icon:lastPage')}
              data-testid="last-page-button"
              onClick={() => gotoPage(totalPageCount - 1)}
              isDisabled={!canNextPage}
            >
              <FontAwesomeIcon icon={faChevronDoubleRight} />
            </IconButton>
          </>
        ) : null}
      </nav>
    </HStack>
  );
};
